import { useRef, useState, useEffect } from "react";
import { Box, Container, Typography, IconButton, Skeleton, Alert } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { postData } from "../../Webservice/Webservice";
import { useNavigate } from "react-router-dom";

const rubikfont = {
  fontFamily: "Rubik, san-serif"
};

const placeholderImage = "https://via.placeholder.com/250x250?text=No+Image";

function Category() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const scrollContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const navigate = useNavigate();

  const handlesubcategory = (categoryId, categoryTitle) => {
    const categorySlug = categoryTitle?.toLowerCase().replace(/\s+/g, '-') || 'category';
    navigate(`/${categorySlug}`, { state: { categoryId, categoryTitle } });
  };

  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const tokenValue = sessionStorage.getItem("token");
        const token = tokenValue ? `Bearer ${tokenValue}` : "";
        const response = await postData('category/list', {}, token);

        if (response) {
          const activeCategories = response.categories.filter(
            category => category.status === 'active'
          );
          setCategories(activeCategories);
        }
      } catch (err) {
        console.error('Error fetching categories:', err);
        setError('Failed to load categories. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  const checkScrollButtons = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 300;
      const newPosition =
        direction === "left"
          ? scrollContainerRef.current.scrollLeft - scrollAmount
          : scrollContainerRef.current.scrollLeft + scrollAmount;

      scrollContainerRef.current.scrollTo({
        left: newPosition,
        behavior: "smooth",
      });

      setTimeout(checkScrollButtons, 500);
    }
  };

  // Loading state with skeleton
  if (loading) {
    return (
      <Box sx={{ py: { xs: 6, md: 10 }, padding: "0px 15px 0px 15px" }}>
        <Typography
          variant="h4"
          align="center"
          sx={{
            fontSize: { xs: "32px", sm: "48px", md: "56px" },
            color: "#222",
            ...rubikfont,
            fontWeight: 900,
            lineHeight: 1.1,
            letterSpacing: "3.5px",
            textTransform: "uppercase",
            mb: 4
          }}
        >
          Our Categories
        </Typography>

        <Box sx={{ position: "relative" }}>
          <Box
            sx={{
              display: "flex",
              gap: { xs: 2, sm: 3, md: 4 },
              overflowX: "auto",
              "&::-webkit-scrollbar": { display: "none" },
              msOverflowStyle: "none",
              scrollbarWidth: "none",
              py: 2,
              px: { xs: 5, sm: 6, md: 8 },
            }}
          >
            {[...Array(6)].map((_, index) => (
              <Box
                key={index}
                sx={{
                  minWidth: { xs: 160, sm: 200, md: 250 },
                  textAlign: "center",
                  flexShrink: 0,
                }}
              >
                <Skeleton
                  variant="circular"
                  sx={{
                    width: { xs: 160, sm: 200, md: 250 },
                    height: { xs: 160, sm: 200, md: 250 },
                    mb: 2,
                  }}
                />
                <Skeleton
                  variant="text"
                  sx={{
                    width: { xs: 120, sm: 150, md: 180 },
                    height: { xs: 20, sm: 24, md: 28 },
                    mx: "auto",
                  }}
                />
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Container sx={{ py: { xs: 6, md: 10 } }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  // No categories found
  if (categories.length === 0) {
    return (
      <Container sx={{ py: { xs: 6, md: 10 } }}>
        <Typography
          variant="h6"
          align="center"
          sx={{ color: 'text.secondary' }}
        >
          No categories available at the moment.
        </Typography>
      </Container>
    );
  }

  return (
    <Box sx={{ py: { xs: 6, md: 10 }, padding: "0px 15px 0px 15px" }}>
      <Typography
        variant="h4"
        align="center"
        sx={{
          fontSize: { xs: "32px", sm: "48px", md: "56px" },
          color: "#222",
          ...rubikfont,
          fontWeight: 900,
          lineHeight: 1.1,
          letterSpacing: "3.5px",
          textTransform: "uppercase",
          mb: 4
        }}
      >
        Our Categories
      </Typography>

      <Box sx={{ position: "relative" }}>
        {/* Left Arrow */}
        <IconButton
          onClick={() => scroll("left")}
          disabled={!canScrollLeft}
          sx={{
            position: "absolute",
            left: { xs: 0, sm: -20 },
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 2,
            bgcolor: "white",
            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
            "&:hover": { bgcolor: "#f5f5f5" },
            "&.Mui-disabled": { opacity: 0.3 },
          }}
        >
          <ChevronLeft />
        </IconButton>

        {/* Scrollable Container */}
        <Box
          ref={scrollContainerRef}
          onScroll={checkScrollButtons}
          sx={{
            display: "flex",
            gap: { xs: 2, sm: 3, md: 4 },
            overflowX: "auto",
            scrollBehavior: "smooth",
            "&::-webkit-scrollbar": { display: "none" },
            msOverflowStyle: "none",
            scrollbarWidth: "none",
            py: 2,
            px: { xs: 5, sm: 6, md: 8 },
          }}
        >
          {categories.map((category) => (
            <Box
              key={category._id}
              sx={{
                minWidth: { xs: 160, sm: 200, md: 250 },
                textAlign: "center",
                flexShrink: 0,
              }}
            >
              <Box
                sx={{
                  width: { xs: 160, sm: 200, md: 250 },
                  height: { xs: 160, sm: 200, md: 250 },
                  borderRadius: "50%",
                  overflow: "hidden",
                  boxShadow: "0 6px 20px rgba(0,0,0,0.1)",
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  "&:hover": {
                    transform: "scale(1.05)",
                    boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
                  },
                }}
                onClick={() => handlesubcategory(category._id, category.title)}
              >
                <img
                  src={category.image || placeholderImage}
                  alt={category.title}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = placeholderImage;
                  }}
                />
              </Box>
              <Typography
                variant="subtitle1"
                sx={{
                  mt: 2,
                  fontWeight: 600,
                  color: "#222",
                  ...rubikfont,
                  fontSize: { xs: "14px", sm: "16px", md: "18px" },
                }}
              >
                {category.title}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Right Arrow */}
        <IconButton
          onClick={() => scroll("right")}
          disabled={!canScrollRight}
          sx={{
            position: "absolute",
            right: { xs: 0, sm: -20 },
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 2,
            bgcolor: "white",
            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
            "&:hover": { bgcolor: "#f5f5f5" },
            "&.Mui-disabled": { opacity: 0.3 },
          }}
        >
          <ChevronRight />
        </IconButton>
      </Box>
    </Box>
  );
}

export default Category;